import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  FamilyMember,
  FamilyMemberCreateRequest,
  FamilyMemberUpdateRequest,
  FamilyMemberResponse,
  FamilyMembersResponse,
} from '#models/db/family-member/family-member.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { logger } from '#lib/logger';
import { IFamilyMembersService } from './family-members.interface';

@Injectable({
  providedIn: 'root',
})
export class FamilyMembersService implements IFamilyMembersService {
  path = 'family-details';
  createFamilyMemberLoading$ = new Subject<boolean>();
  deleteFamilyMemberLoading$ = new Subject<boolean>();
  getFamilyMemberLoading$ = new Subject<boolean>();
  getFamilyMembersLoading$ = new Subject<boolean>();
  updateFamilyMemberLoading$ = new Subject<boolean>();
  activeFamilyMember$ = new BehaviorSubject<FamilyMember | null>(null);
  familyMembers$ = new BehaviorSubject<FamilyMember[] | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  async createFamilyMember(
    req: FamilyMemberCreateRequest,
    nextFn?: (data: FamilyMember | null) => void
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.createFamilyMemberLoading$,
        subject: this.activeFamilyMember$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<
          FamilyMemberResponse,
          FamilyMemberCreateRequest
        >({ url: this.path }, req);

        logger.log(response, 'FamilyMembersService.createFamilyMember -> response');

        return response.details;
      }
    );
  }

  async deleteFamilyMember(
    id: string,
    nextFn?: (data: FamilyMember | null) => void
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteFamilyMemberLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.delete<FamilyMemberResponse>({
          url: `${this.path}/${id}`,
        });

        const current = this.familyMembers$.value;

        if (current) {
          this.familyMembers$.next(current.filter((member) => member.id !== response.details.id));
        }

        return response.details;
      }
    );
  }

  async getFamilyMembers(
    nextFn?: (data: FamilyMember[] | null) => void,
    silent?: boolean
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getFamilyMembersLoading$ : undefined,
        subject: this.familyMembers$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<FamilyMembersResponse>({ url: this.path });

        logger.log(response.details, 'FamilyMembersService.getFamilyMembers -> response');

        return response.details;
      }
    );
  }

  async getFamilyMemberById(
    id: string,
    nextFn?: (data: FamilyMember | null) => void,
    silent?: boolean
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getFamilyMemberLoading$ : undefined,
        subject: this.activeFamilyMember$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<FamilyMemberResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'FamilyMembersService.getFamilyMemberById -> response');

        return response.details;
      }
    );
  }

  async updateFamilyMember(
    req: FamilyMemberUpdateRequest,
    nextFn?: (data: FamilyMember | null) => void
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateFamilyMemberLoading$,
        subject: this.activeFamilyMember$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          FamilyMemberResponse,
          FamilyMemberUpdateRequest
        >({ url: `${this.path}/${req.id}` }, req);

        logger.log(response, 'FamilyMembersService.updateFamilyMember -> response');

        return response.details;
      }
    );
  }

  setActiveFamilyMember(fm: FamilyMember | null) {
    this.activeFamilyMember$.next(fm);
  }

  resetFamilyMembers() {
    this.familyMembers$.next(null);
    this.activeFamilyMember$.next(null);
    logger.log('complete', 'FamilyMembersService.resetFamilyMembers');
  }
}
